import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { LockedUi } from "components/LockedUi/LockedUi";
import Wind from "components/icons/Wind";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { DeleteCustomReportModal } from "./DeleteCustomReportModal";
import { ReportCard } from "./ReportCard";
import type { UseCustomReports } from "./useCustomReports";

const EmptyState = ({
  onCreateNew,
  isCreating,
}: {
  onCreateNew: () => void;
  isCreating: boolean;
}) => (
  <div className="t-flex t-items-center t-h-full t-w-full t-justify-center t-flex-col t-gap-4">
    <Wind className="t-text-neutral-40" />
    <div className="t-flex t-flex-col t-items-center t-gap-1 t-text-neutral-40">
      <div className="t-text-body-sm t-text-text-60">No custom reports yet</div>
      <p className="t-m-0 t-text-body-sm t-text-center">
        Create and save custom reports for quick access and analysis
      </p>
    </div>
    <div className="t-flex t-items-center t-gap-3">
      <LockedUi
        feature={WRITE_PERMISSION_KEYS.FINANCIALS}
        blockedUi={<LockedButton text="Create new" />}
      >
        <Button
          customType="primary"
          size="small"
          onClick={onCreateNew}
          isLoading={isCreating}
        >
          Create new
        </Button>
      </LockedUi>
    </div>
  </div>
);

export const CustomReportsGrids = ({
  templates,
  savedReports,
  isLoading,
  isSuccess,
  isEmpty,
  isCreating,
  hasWritePermission,
  selectedReportId,
  deleteModal,
  handleCreateNew,
  handleCreateFromTemplate,
  handleCardClick,
  handleEdit,
  handleDeleteClick,
}: UseCustomReports) => (
  <Async.Root isLoading={isLoading} isSuccess={isSuccess} isEmpty={isEmpty}>
    <Async.Empty>
      <EmptyState onCreateNew={handleCreateNew} isCreating={isCreating} />
    </Async.Empty>

    <Async.Success>
      <div className="t-flex t-flex-col t-gap-5">
        {templates.length > 0 && (
          <div className="t-flex t-flex-col t-gap-3">
            <p className="t-m-0 t-text-body-sm t-text-text-30 t-font-medium">
              Create with Template
            </p>
            <div className="t-grid t-grid-cols-2 t-gap-4">
              {templates.map((template) => (
                <ReportCard
                  key={template.uuid}
                  report={template}
                  onClick={() => handleCreateFromTemplate(template.uuid)}
                />
              ))}
            </div>
          </div>
        )}

        {savedReports.length > 0 && (
          <div className="t-flex t-flex-col t-gap-3">
            <p className="t-m-0 t-text-body-sm t-text-text-30 t-font-medium">
              Saved Reports
            </p>
            <div className="t-grid t-grid-cols-2 t-gap-4">
              {savedReports.map((report) => (
                <ReportCard
                  key={report.uuid}
                  report={report}
                  onClick={() => handleCardClick(report)}
                  onEdit={
                    hasWritePermission ? () => handleEdit(report) : undefined
                  }
                  onDelete={
                    hasWritePermission
                      ? () => handleDeleteClick(report)
                      : undefined
                  }
                />
              ))}
            </div>
          </div>
        )}

        <DeleteCustomReportModal
          isOpen={deleteModal.isOpen}
          close={deleteModal.close}
          reportId={selectedReportId}
        />
      </div>
    </Async.Success>
  </Async.Root>
);
